/* @flow */

import * as React from 'react';
import { compose } from 'ramda';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

// Styles
import styles from './MenuPopoverItem.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import withForwardRef from '@braindate/util/lib/react/hoc/withForwardRef';

// Components
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Box, Divider } from '@mui/material';

// Icons

export type MenuPopoverItemProps = {
  icon?: React.Node,
  title: string,
  subtitle?: string,
  isActive?: boolean,
  to?: string,
  hasDivider?: boolean,
  onClick?: (() => void) | (() => Promise<void>),
  onMouseOver?: (() => void) | (() => Promise<void>),
};

type Props = {
  forwardedRef: { current: HTMLElement | null },
};

const useStyles = createUseThemeStyles(styles);

const MenuPopoverItem = ({
  icon,
  title,
  subtitle,
  isActive,
  to,
  hasDivider,
  onClick,
  onMouseOver,
  forwardedRef,
  ...props
}: Props & MenuPopoverItemProps): React.Node => {
  const classes = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <MenuItem
      classes={{
        root: classNames(
          classes.menuItem,
          isActive && classes.menuItemActive,
          'ignore-focus',
        ),
      }}
      onClick={onClick}
      onMouseOver={onMouseOver}
      ref={forwardedRef}
      {...(to ? { component: Link, to, href: to } : {})}
      {...props}
    >
      <Box
        className={classNames(classes.menuContent, {
          [classes.withDivider]: hasDivider,
        })}
      >
        {icon && (
          <ListItemIcon>
            <div className={classNames(classes.iconContainer)}>{icon}</div>
          </ListItemIcon>
        )}

        <ListItemText disableTypography className={classes.title}>
          <Typography classes={{ root: classes.text }}>{title}</Typography>

          {subtitle ? (
            <Typography variant="caption">{subtitle}</Typography>
          ) : null}
        </ListItemText>
      </Box>

      {hasDivider ? <Divider className={classes.divider} /> : null}
    </MenuItem>
  );
};

export default compose(React.memo, withForwardRef)(MenuPopoverItem);
